import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const UNO_STAGE = {
  in_invio: "Invio al portale in corso…",
  mnp_codice: "Serve il codice SMS ricevuto dal cliente per la portabilità",
  da_confermare: "Riepilogo pronto: manca la conferma dell'admin",
  conferma_in_corso: "Conferma in corso…",
  inserita: "Inserita sul portale: in verifica da Uno Mobile",
  eseguita: "Eseguita sul portale",
  errore: "Invio non riuscito",
};
const RUNNING = ["in_invio", "conferma_in_corso"];
const when = (v) => (v ? new Date(v).toLocaleString("it-IT") : "");
const err = (e, f) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || f);

const usePoll = (active, fn) => {
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(fn, 3000);
    return () => clearInterval(t);
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
};

const Steps = ({ steps = [] }) => (steps.length ? <div className="space-y-1" data-testid="uno-steps">
  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Passaggi sul portale</p>
  {steps.slice().reverse().slice(0, 12).map((s, i) => <div key={i} className="grid grid-cols-[auto_minmax(0,1fr)_auto] gap-2 text-xs border-b border-border py-1">
    <span className={s.ok ? "text-teal-600" : "text-destructive"}>{s.ok ? "OK" : "Errore"}</span>
    <span className="break-words"><span className="font-medium">{s.step}</span>{s.msg ? ` · ${s.msg}` : ""}</span>
    <span className="text-muted-foreground">{when(s.at)}</span>
  </div>)}
</div> : null);

const PortalPage = ({ page, title, testid }) => (page ? <details className="rounded border border-border px-2 py-1" data-testid={testid}>
  <summary className="text-xs cursor-pointer">{title}</summary>
  {page.errors?.length > 0 && <p className="text-xs text-destructive mt-1">{page.errors.join(" · ")}</p>}
  <p className="text-[11px] text-muted-foreground mt-1 whitespace-pre-wrap max-h-56 overflow-y-auto">{page.text}</p>
  {page.buttons?.length > 0 && <p className="text-[11px] mt-1">Pulsanti: {page.buttons.join(" · ")}</p>}
</details> : null);

export const MnpCodeForm = ({ request, onChanged }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/italiamobile/requests/${request.id}/unomobile-mnp-code`, { code }); setCode(""); toast.success("Codice inviato: continuo l'attivazione sul portale"); onChanged(data.request); }
    catch (e) { err(e, "Codice non inviato"); } finally { setBusy(false); }
  };
  const resend = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/italiamobile/requests/${request.id}/unomobile-mnp-resend`); toast.success("SMS inviato di nuovo al numero da portare"); onChanged(data.request); }
    catch (e) { err(e, "SMS non inviato"); } finally { setBusy(false); }
  };
  return <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid="uno-mnp-code">
    <p className="text-sm font-medium">Codice per la portabilità</p>
    <p className="text-xs text-muted-foreground">Il cliente ha ricevuto un SMS sul numero da portare: scrivi qui il codice.</p>
    <div className="flex flex-wrap gap-2">
      <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="Codice" className="w-36 text-center font-mono tracking-widest" data-testid="uno-mnp-code-input" />
      <Button size="sm" disabled={busy || code.length < 4} onClick={send} data-testid="uno-mnp-code-send">Conferma codice</Button>
      <Button size="sm" variant="outline" disabled={busy} onClick={resend} data-testid="uno-mnp-code-resend">Invia di nuovo l'SMS</Button>
    </div>
  </div>;
};

export const UnoMobileRequestBox = ({ request, onChanged }) => {
  const [pre, setPre] = useState(null);
  const [busy, setBusy] = useState(false);
  const uno = request.operator === "unomobile";
  const um = request.unomobile || {};
  const refresh = () => api.get(`/admin/italiamobile/requests/${request.id}/unomobile`).then(({ data }) => { setPre(data); onChanged(data.request); }).catch(() => {});
  useEffect(() => { if (uno) api.get(`/admin/italiamobile/requests/${request.id}/unomobile`).then(({ data }) => setPre(data)).catch(() => {}); }, [request.id, uno]);
  usePoll(uno && (um.busy || RUNNING.includes(um.stage)), refresh);
  if (!uno) return null;
  const act = async (path, ok) => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/italiamobile/requests/${request.id}/${path}`); toast.success(data.result ? (data.result.ok ? data.result.summary : data.result.reason) : ok); if (data.request) onChanged(data.request); }
    catch (e) { err(e, "Operazione non riuscita"); } finally { setBusy(false); }
  };
  const closed = ["annullata", "completata"].includes(request.status);
  const canSend = !closed && !um.busy && !["da_confermare", "inserita", "mnp_codice", ...RUNNING].includes(um.stage);
  return <div className="pt-3 space-y-2" data-testid="uno-request-box">
    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Portale Uno Mobile</p>
    {um.stage && <p className={`text-sm ${um.stage === "errore" ? "text-destructive" : "text-primary"}`} data-testid="uno-stage">{UNO_STAGE[um.stage] || um.stage}</p>}
    {[["Cliente sul portale", um.customer_id], ["Abbonamento", um.subscription_id], ["Numero assegnato", um.msisdn || request.assigned_number], ["Stato sul portale", um.task?.status && `${um.task.status}${um.task.mnp ? ` · portabilità: ${um.task.mnp}` : ""}`]].filter(([, v]) => v).map(([k, v]) => <div key={k} className="flex justify-between gap-3 text-xs"><span className="text-muted-foreground">{k}</span><span className="font-mono text-right">{v}</span></div>)}
    {pre && !pre.on && <p className="text-xs text-amber-600" data-testid="uno-off">Collegamento al portale spento: accendilo nelle impostazioni.</p>}
    {pre && pre.missing.length > 0 && <p className="text-xs text-amber-600" data-testid="uno-missing">Mancano: {pre.missing.join(", ")}</p>}
    {um.stage === "da_confermare" && <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="uno-review">
      <p className="text-sm font-medium">Riepilogo mostrato dal portale</p>
      <p className="text-[11px] text-muted-foreground whitespace-pre-wrap max-h-56 overflow-y-auto" data-testid="uno-review-text">{um.review?.text}</p>
      <Button size="sm" disabled={busy} onClick={() => act("unomobile-confirm", "Conferma avviata")} data-testid="uno-confirm">Conferma attivazione su Uno Mobile</Button>
    </div>}
    {um.stage === "mnp_codice" && <MnpCodeForm request={request} onChanged={onChanged} />}
    <div className="flex flex-wrap gap-2">
      {canSend && <Button size="sm" disabled={busy || (pre && (!pre.on || pre.missing.length > 0))} onClick={() => act("unomobile-send", "Invio al portale avviato")} data-testid="uno-send">{um.stage === "errore" ? "Riprova l'invio a Uno Mobile" : "Invia a Uno Mobile"}</Button>}
      {um.subscription_id && um.stage === "inserita" && <Button size="sm" variant="outline" disabled={busy} onClick={() => act("unomobile-sync", "Stato aggiornato")} data-testid="uno-sync">Controlla stato</Button>}
    </div>
    {um.stage === "errore" && <PortalPage page={um.page} title="Cosa ha mostrato il portale" testid="uno-error-page" />}
    {um.stage === "inserita" && <PortalPage page={um.result} title="Risposta del portale alla conferma" testid="uno-result-page" />}
    <Steps steps={um.steps} />
  </div>;
};

export const UnoMobileDealerBox = ({ request, onChanged }) => {
  const uno = request.operator === "unomobile";
  const um = request.unomobile || {};
  const refresh = () => api.get("/italiamobile/requests", { params: { operator: "unomobile" } }).then(({ data }) => { const r = (data || []).find((x) => x.id === request.id); if (r) onChanged(r); }).catch(() => {});
  usePoll(uno && (um.busy || RUNNING.includes(um.stage)), refresh);
  if (!uno || !um.stage) return null;
  return <div className="rounded-lg border border-border p-3 space-y-2" data-testid="dealer-uno-box">
    <p className="text-sm font-semibold">Portale Uno Mobile</p>
    <p className={`text-sm ${um.stage === "errore" ? "text-destructive" : ""}`} data-testid="dealer-uno-stage">{UNO_STAGE[um.stage] || um.stage}</p>
    {request.provider_status && <p className="text-xs text-muted-foreground" data-testid="dealer-uno-summary">{request.provider_status}</p>}
    {um.stage === "mnp_codice" && <MnpCodeForm request={request} onChanged={onChanged} />}
  </div>;
};
